import type { ColumnDescription } from 'react-bootstrap-table-next';
import { nameAgeURFormatter, nameURFormatter, priorityFormatter } from '..';
import {
  namesFormatter,
  imageFormatter,
  medsFormatter,
  syncFormatter,
  syncHeaderFormatter,
  mhrHeaderFormatter,
  commHeaderFormatter,
  bulletListFormatter,
  episodeFormatter,
  assignFormatter,
  bulletListCommaFormatter,
  insightsHeaderFormatter,
  notesFormatter,
} from '../PatientListTableFormatter';
import { ActionFormatterComponent } from '../components/formatters';
import moment from 'moment';
import FavouriteFomatter from '../components/formatters/FavouriteFomatter';
import TagsFomatter from '../components/formatters/TagsFomatter';
import ActiveInsightsFormatter from '../components/formatters/ActiveInsightsFormatter';
import WithingsCommunicationFormatter from '../components/formatters/WithingsCommunicationFormatter';
import { ColumnIds } from '../../../hooks/types';
import careProgressColumns from '../components/CareProgress/columns';
import AmountMedicineFormatter from '../components/formatters/AmountMedicineFormatter';
import RenalFunctionFormatter from '../components/formatters/RenalFunctionFormatter';
import EpisodeDetailsFormatter from '../components/formatters/EpisodeDetailsFormatter';
import LastPharmacyNoteDateFormatter from '../components/formatters/LastPharmacyNoteDateFormatter';
import LastAdmissionFormatter from '../components/formatters/LastAdmissionFormatter';
import LastEpisodeDetailsFormatter from '../components/formatters/LastEpisodeDetailsFormatter';
import { convertToFormattedDateString, formatAge } from '../../../helpers';

export type ColumnBuildCtx = {
  isMyPainHealthEligible?: (row: any) => boolean;
};

type ColumnBuildResult = {
  columns: any[];
  deferToEnd?: boolean;
};

export const DEFAULT_PATIENT_LIST_COLUMN_IDS: string[] = [
  ColumnIds.ASSIGNED,
  ColumnIds.FAVOURITE,
  'first_name',
  'last_name',
  'notes',
  'clinicsData',
  'primaryTeams',
  'healthProfessionals',
  'distressTrend',
  'clinicalProfileTrend',
  'activeInsights',
  'withingsSleepData',
  'lastInteraction',
  'communication',
  'lastEpisode',
  'sync',
];

export const DEIDENTIFIED_PATIENT_LIST_COLUMN_IDS: string[] = [
  ColumnIds.CAREMAPPR_ID,
  ColumnIds.ACTIVE_INSIGHTS,
  ColumnIds.MYPAINHEALTH_APP_ELIGIBILITY,
  ColumnIds.MYPAINHEALTH_INVITED,
  ColumnIds.MYPAINHEALTH_CONSENT,
  ColumnIds.MYPAINHEALTH_LAST_LOGIN,
];

const createPatientListColumnRegistry = (): Record<
  string,
  (ctx: ColumnBuildCtx) => ColumnBuildResult
> => ({
  [ColumnIds.ASSIGNED]: () => ({
    columns: [
      {
        dataField: 'assigned',
        text: '',
        sort: true,
        classes: 'no-title-table-header',
        headerSortingClasses: 'sortingColumn',
        formatter: assignFormatter,
      },
    ],
  }),
  [ColumnIds.FAVOURITE]: () => ({
    columns: [
      {
        dataField: 'favourite',
        text: '',
        sort: true,
        classes: 'no-title-table-header col3',
        headerSortingClasses: 'sortingColumn',
        formatter: (_: any, row: any) => (
          <FavouriteFomatter
            key={`favourite-${row.id}`}
            id={`favourite-${row.id}`}
            rowData={row}
            disabled
          />
        ),
      },
    ],
  }),
  name: () => ({
    columns: [
      {
        dataField: 'first_name',
        text: 'First name',
        sort: true,
        headerSortingClasses: 'sortingColumn',
        classes: 'col8',
        formatter: namesFormatter,
      },
      {
        dataField: 'last_name',
        text: 'Last name',
        sort: true,
        formatter: namesFormatter,
        headerSortingClasses: 'sortingColumn',
        classes: 'col8',
      },
    ],
  }),
  clinics: () => ({
    columns: [
      {
        dataField: 'clinicsData',
        text: 'Services',
        classes: 'col6',
        formatter: bulletListCommaFormatter,
      },
    ],
  }),
  latest_withings_communication: () => ({
    columns: [
      {
        dataField: 'withingsSleepData',
        text: 'Latest withings communication',
        classes: 'col12',
        formatter: (_: any, row: any) => (
          <WithingsCommunicationFormatter
            key={`withings-communication-${row.id}`}
            id={`withings-communication-${row.id}`}
            data={row}
          />
        ),
      },
    ],
  }),
  episode_status: () => ({
    columns: [
      {
        dataField: 'lastEpisode',
        text: 'Episode',
        classes: 'col6',
        formatter: episodeFormatter,
      },
    ],
  }),
  first_name: () => ({
    columns: [
      {
        dataField: 'first_name',
        text: 'First name',
        sort: true,
        headerSortingClasses: 'sortingColumn',
        classes: 'col8',
        formatter: namesFormatter,
      },
    ],
  }),
  last_name: () => ({
    columns: [
      {
        dataField: 'last_name',
        text: 'Last name',
        sort: true,
        formatter: namesFormatter,
        headerSortingClasses: 'sortingColumn',
        classes: 'col8',
      },
    ],
  }),
  notes: () => ({
    columns: [
      {
        dataField: 'notes',
        text: 'Tags',
        classes: 'col6',
        formatter: (cell: any, row: any) => (
          <TagsFomatter
            key={`tags-${row.id}`}
            id={`tags-${row.id}`}
            data={cell}
          />
        ),
      },
    ],
  }),
  clinicsData: () => ({
    columns: [
      {
        dataField: 'clinicsData',
        text: 'Services',
        classes: 'col6',
        formatter: bulletListCommaFormatter,
      },
    ],
  }),
  primaryTeams: () => ({
    columns: [
      {
        dataField: 'primaryTeams',
        text: 'Primary teams',
        classes: 'col8',
        formatter: bulletListFormatter,
      },
    ],
  }),
  healthProfessionals: () => ({
    columns: [
      {
        dataField: 'healthProfessionals',
        text: 'HP',
        classes: 'col8 healthProfessionals',
        formatter: bulletListCommaFormatter,
      },
    ],
  }),
  distressTrend: () => ({
    columns: [
      {
        dataField: 'distressTrend',
        text: 'Distress trend',
        classes: 'col3',
        formatter: (cell: unknown, row: unknown) =>
          imageFormatter(cell, row, 'distressTrend'),
      },
    ],
  }),
  clinicalProfileTrend: () => ({
    columns: [
      {
        dataField: 'clinicalProfileTrend',
        text: 'Clinical profile trend',
        classes: 'col3',
        formatter: (cell: unknown, row: unknown) =>
          imageFormatter(cell, row, 'clinicalProfileTrend'),
      },
    ],
  }),
  activeInsights: () => ({
    columns: [
      {
        dataField: 'activeInsights',
        text: 'Active insights',
        classes: 'col12',
        formatter: (cell: any, row: any) => (
          <ActiveInsightsFormatter
            key={`active-insights-${row.id}`}
            id={`active-insights-${row.id}`}
            data={cell}
          />
        ),
        headerFormatter: insightsHeaderFormatter,
        headerSortingClasses: 'sortingColumn',
        sort: true,
      },
    ],
  }),
  withingsSleepData: () => ({
    columns: [
      {
        dataField: 'withingsSleepData',
        text: 'Latest withings communication',
        classes: 'col12',
        formatter: (_: any, row: any) => (
          <WithingsCommunicationFormatter
            key={`withings-communication-${row.id}`}
            id={`withings-communication-${row.id}`}
            data={row}
          />
        ),
      },
    ],
  }),
  lastInteraction: () => ({
    columns: [
      {
        dataField: 'lastInteraction',
        text: 'Latest MHR data',
        classes: 'col10',
        headerFormatter: mhrHeaderFormatter,
      },
    ],
  }),
  communication: () => ({
    columns: [
      {
        dataField: 'communication',
        text: 'Latest patient communication',
        classes: 'col16',
        formatter: medsFormatter,
        headerFormatter: commHeaderFormatter,
      },
    ],
  }),
  lastEpisode: () => ({
    columns: [
      {
        dataField: 'lastEpisode',
        text: 'Episode',
        classes: 'col6',
        formatter: episodeFormatter,
      },
    ],
  }),
  sync: () => ({
    columns: [
      {
        dataField: 'sync',
        text: 'Sync',
        classes: 'col4',
        formatter: syncFormatter,
        headerFormatter: syncHeaderFormatter,
      },
    ],
  }),

  [ColumnIds.PRIORITY]: () => ({
    columns: [
      {
        text: 'Priority',
        sort: false,
        classes: 'col8',
        formatter: priorityFormatter,
      },
    ],
  }),
  [ColumnIds.PATIENT_NAME_AGE_UR]: () => ({
    columns: [
      {
        text: 'Name, age + UR',
        sort: true,
        classes: 'col8',
        formatter: nameAgeURFormatter,
        dataField: 'name',
      },
    ],
  }),
  [ColumnIds.LOCATION]: () => ({
    columns: [
      {
        text: 'Location',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <EpisodeDetailsFormatter datakey="Location" row={row} />
        ),
      },
    ],
  }),
  [ColumnIds.TAGS_PREFERENCES]: () => ({
    columns: [
      {
        dataField: 'notes',
        text: 'Tags',
        sort: false,
        classes: 'col4',
        formatter: notesFormatter,
      },
    ],
  }),
  [ColumnIds.RENAL_FUNCTION]: () => ({
    columns: [
      {
        text: 'Renal function',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => <RenalFunctionFormatter row={row} />,
      },
    ],
  }),
  [ColumnIds.NO_MEDS]: () => ({
    columns: [
      {
        text: (
          <>
            <div>No.</div>
            <div>meds</div>
          </>
        ),
        sort: false,
        classes: 'col4',
        formatter: (_: any, row: any) => <AmountMedicineFormatter row={row} />,
      },
    ],
  }),
  [ColumnIds.REASON_FOR_ADMISSION]: () => ({
    columns: [
      {
        text: 'Reason for admission',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <EpisodeDetailsFormatter
            type="single"
            datakey={['Admission reason', 'EMR Admission reason']}
            row={row}
          />
        ),
      },
    ],
  }),
  [ColumnIds.ROUTE_MODIFIERS]: () => ({
    columns: [
      {
        text: 'Route modifiers',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <EpisodeDetailsFormatter datakey="Route modifiers" row={row} />
        ),
      },
    ],
  }),
  [ColumnIds.ALLERGIES]: () => ({
    columns: [
      {
        text: 'Allergies',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <EpisodeDetailsFormatter
            datakey={['Allergies', 'allergies']}
            row={row}
          />
        ),
      },
    ],
  }),
  [ColumnIds.LAST_PHARMACY_NOTE_DATE]: () => ({
    columns: [
      {
        text: 'Last Pharmacy event',
        sort: false,
        classes: 'col6',
        formatter: (_: any, row: any) => (
          <LastPharmacyNoteDateFormatter row={row} />
        ),
      },
    ],
  }),
  [ColumnIds.LAST_ADMISSION]: () => ({
    columns: [
      {
        text: 'Last admission',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => <LastAdmissionFormatter row={row} />,
      },
    ],
  }),
  [ColumnIds.EPISODE_CLOSED_DATE]: () => ({
    columns: [
      {
        text: 'D/C date',
        sort: false,
        classes: 'col6',
        formatter: (_: any, row: any) => (
          <EpisodeDetailsFormatter
            isDate
            type="single"
            datakey={['Planned D/C', 'EMR Estimated D/C Date']}
            row={row}
          />
        ),
      },
    ],
  }),
  [ColumnIds.SERVICE]: () => ({
    columns: [
      {
        text: 'Service',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <LastEpisodeDetailsFormatter row={row} fieldKey="Service" />
        ),
      },
    ],
  }),
  [ColumnIds.TEAM]: () => ({
    columns: [
      {
        text: 'Team',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => {
          const teams = row?.teams;
          if (!teams || !Array.isArray(teams) || teams.length === 0) {
            return <span>-</span>;
          }
          return <span>{teams.join(', ')}</span>;
        },
      },
    ],
  }),
  [ColumnIds.ADMITTING_DOCTOR]: () => ({
    columns: [
      {
        text: 'Admitting Dr',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <LastEpisodeDetailsFormatter row={row} fieldKey="Admitting Dr." />
        ),
      },
    ],
  }),
  [ColumnIds.ADMISSION_STATUS]: () => ({
    columns: [
      {
        text: 'Admission Status',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <LastEpisodeDetailsFormatter
            row={row}
            fieldKey="internalVisitStatus"
          />
        ),
      },
    ],
  }),
  [ColumnIds.ADMISSION_DATE]: () => ({
    columns: [
      {
        text: 'Admission Date',
        sort: true,
        classes: 'col8',
        dataField: ColumnIds.ADMISSION_DATE,
        formatter: (_: any, row: any) => (
          <LastEpisodeDetailsFormatter row={row} fieldKey="Date admitted" />
        ),
      },
    ],
  }),
  [ColumnIds.GOAL_OF_ADMISSION]: () => ({
    columns: [
      {
        text: 'Goal of admission',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <LastEpisodeDetailsFormatter row={row} fieldKey="Goal of admission" />
        ),
      },
    ],
  }),
  [ColumnIds.LENGTH_OF_STAY]: () => ({
    columns: [
      {
        text: 'Length of stay',
        sort: false,
        classes: 'col8',
        formatter: (_: any, row: any) => (
          <LastEpisodeDetailsFormatter row={row} fieldKey="Length of stay" />
        ),
      },
    ],
  }),
  [ColumnIds.CARE_PROGRESS]: () => ({
    columns: careProgressColumns,
    deferToEnd: true,
  }),
  [ColumnIds.CAREMAPPR_ID]: () => ({
    columns: [
      {
        text: 'CareMappr ID',
        sort: false,
        classes: 'col8',
        dataField: 'access_token',
        formatter: (_: any, column: any) => (
          <span>{column.access_token || '-'}</span>
        ),
      },
    ],
  }),
  [ColumnIds.NAME_UR]: () => ({
    columns: [
      {
        text: 'Name + UR',
        classes: 'col8',
        formatter: nameURFormatter,
        dataField: 'nameUr',
      },
    ],
  }),
  [ColumnIds.DOB_AGE]: () => ({
    columns: [
      {
        text: 'DoB and age',
        sort: true,
        classes: 'col8',
        dataField: 'dob',
        formatter: (_: any, row: any) => {
          const dob = row?.dob;
          const dobText = dob
            ? convertToFormattedDateString(dob, 'DD-MMM-YYYY')
            : '';
          if (!dobText) {
            return <span>-</span>;
          }
          const ageText = formatAge(dob);
          return <span>{`${dobText}${ageText ? ` (${ageText})` : ''}`}</span>;
        },
      },
    ],
  }),
  [ColumnIds.PHENOTYPE]: () => ({
    columns: [
      {
        text: 'Phenotype',
        sort: false,
        classes: 'col8',
        dataField: 'phenotype',
        formatter: (_: any, row: any) => <span>{row?.phenotype || '-'}</span>,
      },
    ],
  }),
  [ColumnIds.DIAGNOSIS]: () => ({
    columns: [
      {
        text: 'Diagnosis',
        sort: false,
        classes: 'col8',
        dataField: 'diagnosis',
        formatter: (_: any, row: any) => <span>{row?.diagnosis || '-'}</span>,
      },
    ],
  }),
  [ColumnIds.TRIAGE_CATEGORY]: () => ({
    columns: [
      {
        text: 'Triage category',
        sort: false,
        classes: 'col8',
        dataField: 'triageCategory',
        formatter: (_: any, row: any) => (
          <span>{row?.triageCategory || '-'}</span>
        ),
      },
    ],
  }),
  [ColumnIds.LAST_EREFERRAL]: () => ({
    columns: [
      {
        text: 'Last eReferral',
        sort: true,
        classes: 'col8',
        dataField: 'lastEreferralDate',
        formatter: (_: any, row: any) => {
          const dateText = row?.lastEreferralDate
            ? convertToFormattedDateString(row.lastEreferralDate, 'DD-MMM-YYYY')
            : '';
          return <span>{dateText || '-'}</span>;
        },
      },
    ],
  }),
  [ColumnIds.ACTIVE_INSIGHTS]: () => ({
    columns: [
      {
        dataField: 'activeInsights',
        text: 'Active insights',
        classes: 'col12',
        formatter: (cell: any, row: any) => (
          <ActiveInsightsFormatter
            key={`active-insights-${row.id}`}
            id={`active-insights-${row.id}`}
            data={cell}
          />
        ),
        headerFormatter: insightsHeaderFormatter,
        headerSortingClasses: 'sortingColumn',
        sort: true,
      },
    ],
  }),
  [ColumnIds.MYPAINHEALTH_APP_ELIGIBILITY]: (ctx: ColumnBuildCtx) => ({
    columns: [
      {
        text: 'mypainhealth app eligibility',
        sort: false,
        classes: 'col8',
        dataField: 'mypainhealthEligible',
        formatter: (_: any, row: any) => {
          const eligible = ctx.isMyPainHealthEligible
            ? ctx.isMyPainHealthEligible(row)
            : false;
          return <span>{eligible ? '✓' : 'Not eligible'}</span>;
        },
      },
    ],
  }),
  [ColumnIds.MYPAINHEALTH_INVITED]: (ctx: ColumnBuildCtx) => ({
    columns: [
      {
        text: 'Invited',
        sort: false,
        classes: 'col6',
        dataField: 'mypainhealthInvited',
        formatter: (cell: any, row: any) => {
          const eligible = ctx.isMyPainHealthEligible
            ? ctx.isMyPainHealthEligible(row)
            : false;
          if (!eligible) {
            return null;
          }
          const invited =
            typeof cell === 'boolean' ? cell : row?.mypainhealthInvited;
          return <span>{invited ? '✓' : 'x'}</span>;
        },
      },
    ],
  }),
  [ColumnIds.MYPAINHEALTH_CONSENT]: (ctx: ColumnBuildCtx) => ({
    columns: [
      {
        text: 'Consent',
        sort: false,
        classes: 'col6',
        dataField: 'mypainhealthConsent',
        formatter: (cell: any, row: any) => {
          const eligible = ctx.isMyPainHealthEligible
            ? ctx.isMyPainHealthEligible(row)
            : false;
          if (!eligible) {
            return null;
          }
          const consent =
            typeof cell === 'boolean' ? cell : row?.mypainhealthConsent;
          return <span>{consent ? '✓' : 'x'}</span>;
        },
      },
    ],
  }),
  [ColumnIds.MYPAINHEALTH_LAST_LOGIN]: (ctx: ColumnBuildCtx) => ({
    columns: [
      {
        text: 'Last login',
        sort: false,
        classes: 'col8',
        dataField: 'mypainhealthLastLogin',
        formatter: (_: any, row: any) => {
          const eligible = ctx.isMyPainHealthEligible
            ? ctx.isMyPainHealthEligible(row)
            : false;
          if (!eligible) {
            return null;
          }
          const dateText = row?.mypainhealthLastLogin
            ? convertToFormattedDateString(
                row.mypainhealthLastLogin,
                'DD-MMM-YYYY',
              )
            : '';
          return <span>{dateText || 'x'}</span>;
        },
      },
    ],
  }),
});

export const resolvePatientListColumns = (
  columnItems: Array<{ id: string; sortable?: boolean }>,
  ctx: ColumnBuildCtx,
) => {
  const registry = createPatientListColumnRegistry();

  const mainColumns: any[] = [];
  const deferredColumns: any[] = [];

  for (const item of columnItems) {
    const builder = registry[item.id];
    if (!builder) {
      continue;
    }
    const result = builder(ctx);
    if (item.id === ColumnIds.NAME_UR && Array.isArray(result.columns)) {
      result.columns = result.columns.map((col) => ({
        ...col,
        sort: typeof item.sortable === 'boolean' ? item.sortable : col.sort,
      }));
    }
    if (result.deferToEnd) {
      deferredColumns.push(...result.columns);
    } else {
      mainColumns.push(...result.columns);
    }
  }

  return [...mainColumns, ...deferredColumns];
};

export const getDefaultColumns = (columnsToHide?: string[]) => {
  const columns = resolvePatientListColumns(
    DEFAULT_PATIENT_LIST_COLUMN_IDS.map((id) => ({ id })),
    {},
  );
  const filteredColumns = columnsToHide
    ? columns?.filter(
        (column) =>
          !columnsToHide.includes(column.text as any) &&
          !columnsToHide.includes(column.dataField as any),
      )
    : columns;
  return filteredColumns;
};

export const getRemoteColumns = (
  columns: ColumnDescription[],
  triggerAssignSomeoneModal: (patientAccessToken: string) => void,
  triggerPatientListUpdate: () => void,
  getClinicianOptions?: (patient: any) => any,
  isAutomedicTenancy: boolean = false,
) => {
  if (!columns) return [];

  const onSelectAssignTo = async (
    value: string,
    patientAccessToken: string,
    callback: (
      isAssigned: boolean,
      patientAccessToken: string,
      clinicianId?: number,
      triggerPatientListUpdate?: () => void,
    ) => Promise<any>,
    clinicianId: number,
  ) => {
    if (value === 'assignToSomeoneElse') {
      triggerAssignSomeoneModal(patientAccessToken);
    } else if (value === 'assignToMe') {
      return callback(true, patientAccessToken, clinicianId);
    }
  };

  const actionColumn: ColumnDescription = {
    dataField: '',
    text: '',
    classes: 'col2',
    sort: false,
    formatter: (_: any, row: any) => {
      const clinicianOptions = getClinicianOptions
        ? getClinicianOptions(row)
        : [];
      const hasClinicianOptions =
        Array.isArray(clinicianOptions) && clinicianOptions.length > 0;
      return (
        <ActionFormatterComponent
          patientId={row?.id}
          recommended={row?.recommended}
          assignedTo={row?.favouritedClinicians}
          onSelectAssignTo={(value, callback, clinicianId) =>
            onSelectAssignTo(value, row?.access_token, callback, clinicianId)
          }
          hasClinicianOptions={hasClinicianOptions}
        />
      );
    },
  };
  return isAutomedicTenancy ? columns.concat([actionColumn]) : columns;
};

export const formatDateString = (dateString: string) => {
  return dateString ? moment(new Date(dateString)).format('DD MMM YYYY') : '';
};
